/* eslint-disable @typescript-eslint/no-explicit-any */
import { TranslationNode } from '@components/Flow';
import { NodeRequestParameter } from '@components/Flow/Nodes';
import { RunWorkflowParameters } from '@store/async/run';

export const getRequestParameters = (
  triggerNode: TranslationNode | undefined,
  parameters: NodeRequestParameter<string>[],
  variableParameters: { [key: string]: NodeRequestParameter<string> },
  savedParameters: { [key: string]: NodeRequestParameter<string> }
) => {
  const getParamValue = (name: string, type: 'query' | 'body' | undefined = undefined) => {
    const saved = Object.values(savedParameters);
    const savedParam = saved.find((param) => param.name === name && (type ? param.type === type : true));
    if (savedParam) return savedParam.value;

    const variables = Object.values(variableParameters);
    const variableParam = variables.find((param) => param.name === name && (type ? param.type === type : true));
    return variableParam?.value || '';
  };

  if (triggerNode?.command_id === 'workflow.module') {
    const moduleParameters: { [key: string]: string } = {};

    for (const param of parameters) {
      moduleParameters[param.name] = getParamValue(param.name);
    }

    return { query: null, body: null, queryData: null, moduleParameters };
  }

  const queryParameters = parameters.filter((param) => param.type === 'query');
  const bodyParameters = parameters.filter((param) => param.type === 'body');
  const query = queryParameters.map((param) => `${param.name}=${getParamValue(param.name, 'query')}`).join('&') || '';

  const queryData: { [key: string]: any } = {};
  const body: { [key: string]: any } = {};

  for (const param of queryParameters) {
    queryData[param.name] = getParamValue(param.name, 'query');
  }

  for (const param of bodyParameters) {
    body[param.name] = getParamValue(param.name, 'body');
  }

  return { query: query ? `&${query}` : '', body, queryData, moduleParameters: null };
};

export const getRequestChatParameters = (
  traceParams: RunWorkflowParameters,
  input: string,
  chatVariable: string,
  prefixes: { [key: string]: string }
) => {
  const { body, queryData, moduleParameters } = traceParams;

  const newQueryData = { ...queryData };
  const newBodyData = { ...body };
  const newModuleData = { ...moduleParameters };

  const inputPrefix = getInputPrefix(chatVariable);
  const isDataPrefix = Object.values(prefixes).some((prefix) => prefix === inputPrefix);

  if (chatVariable.includes('http.request.body.')) {
    if (isDataPrefix) newBodyData[chatVariable.replace(`${inputPrefix}.http.request.body.`, '')] = input;
    else newBodyData[chatVariable.replace('http.request.body.', '')] = input;
  }

  if (chatVariable.includes('http.request.query.')) {
    if (isDataPrefix) newQueryData[chatVariable.replace(`${inputPrefix}.http.request.query.`, '')] = input;
    else newQueryData[chatVariable.replace('http.request.query.', '')] = input;
  }

  if (chatVariable.includes('workflow.module.')) {
    if (isDataPrefix) newModuleData[chatVariable.replace(`${inputPrefix}.workflow.module.`, '')] = input;
    else newModuleData[chatVariable.replace('workflow.module.', '')] = input;
  }

  const query =
    Object.keys(newQueryData)
      .map((paramName) => `${paramName}=${newQueryData[paramName]}`)
      .join('&') || '';

  return {
    query: query ? `&${query}` : '',
    body: newBodyData,
    queryData: newQueryData,
    moduleParameters: newModuleData,
  };
};

const getInputPrefix = (input: string) => {
  if (input.includes('.')) {
    const [prefix] = input.split('.');
    return prefix;
  }

  return input;
};

export const getWorkflowParameters = (node: TranslationNode | undefined): NodeRequestParameter<string>[] => {
  if (!node?.attributes) return [];

  if (node.command_id === 'http.request') {
    const attributes = JSON.parse(node.attributes || '');
    const queryParamAttribute = attributes.find((attribute: any) => attribute.id === 'query_parameters');
    const bodyParamAttribute = attributes.find((attribute: any) => attribute.id === 'body_parameters');

    const queryParams =
      queryParamAttribute?.value.map((item: NodeRequestParameter<string>) => ({
        ...item,
        type: 'query',
      })) || [];

    const bodyParams =
      bodyParamAttribute?.value.map((item: NodeRequestParameter<string>) => ({
        ...item,
        type: 'body',
      })) || [];

    return queryParams.concat(bodyParams);
  }

  if (node.command_id === 'workflow.module') {
    const attributes = JSON.parse(node.attributes || '');
    const parameters = attributes.find((attribute: any) => attribute.id === 'parameters');
    return parameters?.value;
  }

  return [];
};
